<script>
import api from '@/api'
import { alertSuccess } from '@/helpers/swal'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  data() {
    return {
      pagetitle: 'Scheduled Maintenance',
      devices: [],
      device: {
        host: '',
        startTime: '',
        endTime: '',
        remarks: '',
      },
      errors: {
        host: '',
        startTime: '',
        endTime: '',
        remarks: '',
      },
      selectedDevice: null,
      saveBtnDisabled: false,
      deviceId: null,
    }
  },
  components: {
    vSelect,
  },
  computed: {
    minStartTime() {
      const now = new Date()
      now.setSeconds(0, 0) // remove seconds and milliseconds
      return now.toISOString().slice(0, 16) // "YYYY-MM-DDTHH:MM"
    },
  },
  async mounted() {
    await this.getDevices()
    // In case of edit device
    const deviceId = this.$route.params.id
    if (deviceId) {
      await this.getMaintenanceData(deviceId)
      this.deviceId = deviceId
    }
  },
  methods: {
    async getDevices() {
      const params = {
        size: 100,
      }
      await api.get('/devices/getDevices', { params }).then((response) => {
        this.devices = response.data?.devices || []
      })
    },

    async getMaintenanceData(deviceId) {
      await api.get(`/maintenance/device/${deviceId}`).then((response) => {
        this.device = response.data
        this.selectedDevice = this.devices.find((device) => device.host === this.device.host)
      })
    },

    saveDevice() {
      if (!this.validateRequest()) return

      this.saveBtnDisabled = true
      let data = this.device
      data.startTime = this.formatDateTime(this.device.startTime)
      data.endTime = this.formatDateTime(this.device.endTime)
      data.host = this.selectedDevice.host

      this.device.id ? this.updateDevice(data) : this.addDevice(data)
    },

    addDevice(data) {
      api
        .post('/maintenance/device/add', data)
        .then(() => {
          alertSuccess('Schedule added successfully')
          this.$router.push({ name: 'maintenance-schedules' })
        })
        .finally(() => {
          this.saveBtnDisabled = false
        })
    },

    updateDevice(data) {
      api
        .post('/maintenance/device/modify/', data)
        .then(() => {
          alertSuccess('Schedule updated successfully')
          this.$router.push({ name: 'maintenance-schedules' })
        })
        .finally(() => {
          this.saveBtnDisabled = false
        })
    },

    formatDateTime(datetime) {
      if (!datetime.includes('T')) return datetime // already formatted or unchanged
      return datetime.replace('T', ' ') + ':00'
    },

    validateRequest() {
      let isValid = true
      this.errors = {}
      const { startTime, endTime, remarks } = this.device
      if (!this.selectedDevice) {
        this.errors.host = 'Device is required'
        isValid = false
      }
      if (!startTime) {
        this.errors.startTime = 'Start time is required'
        isValid = false
      }
      if (!endTime) {
        this.errors.endTime = 'End time is required'
        isValid = false
      }
      const now = new Date()
      const start = new Date(startTime)
      const end = new Date(endTime)
      if (start < now) {
        this.errors.startTime = 'Start time cannot be in the past'
      }
      if (end <= start) {
        this.errors.endTime = 'End time must be after start time'
      }
      if (!remarks) {
        this.errors.remarks = 'Remarks field is required'
        isValid = false
      }
      return isValid
    },
  },
}
</script>

<template>
  <div class="row">
    <div class="col-lg-6">
      <Teleport to="#elePageTitle">
        {{ pagetitle }}
      </Teleport>
      <div class="card">
        <div class="card-header d-flex align-items-center">
          <h4 class="card-title">{{ $route.params.id ? 'Edit Schedule' : 'Add New Schedule' }}</h4>
        </div>
        <div class="card-body">
          <form @submit.prevent="saveDevice()">
            <div class="row">
              <div class="col-md-12 mb-3">
                <label for="device-input" class="form-label">Device</label>
                <v-select v-model="selectedDevice" :options="devices" label="host" placeholder="Select device" :disabled="deviceId">
                  <template #open-indicator="{ attributes, isOpen }">
                    <i class="font-size-22 fw-semibold bx" :class="isOpen ? 'bx-chevron-up' : 'bx-chevron-down'" v-bind="attributes"></i>
                  </template>
                </v-select>
                <div v-if="errors.host" class="invalid-feedback">{{ errors.host }}</div>
              </div>
              <div class="col-md-6 mb-3">
                <label for="start-time-input" class="form-label">Start Time</label>
                <input type="datetime-local" class="form-control" placeholder="Select start time" id="start-time-input" v-model="device.startTime" :min="minStartTime" />
                <div v-if="errors.startTime" class="invalid-feedback">{{ errors.startTime }}</div>
              </div>
              <div class="col-md-6 mb-3">
                <label for="end-time-input" class="form-label">End Time</label>
                <input type="datetime-local" class="form-control" placeholder="Select end time" id="end-time-input" v-model="device.endTime" :min="minStartTime" />
                <div v-if="errors.endTime" class="invalid-feedback">{{ errors.endTime }}</div>
              </div>
              <div class="col-md-12">
                <label for="remarks-input" class="form-label">Remarks</label>
                <textarea class="form-control" id="remarks-input" placeholder="Write remarks here" rows="5" v-model="device.remarks"></textarea>
                <div v-if="errors.remarks" class="invalid-feedback">{{ errors.remarks }}</div>
              </div>
            </div>
          </form>
        </div>
        <div class="card-footer">
          <div class="row">
            <div class="col-7">
              <router-link to="/maintenance/schedules" class="btn btn-light d-inline-flex align-items-center">
                <i class="bx bx-arrow-back font-size-16 me-1"></i>
                Back to List
              </router-link>
            </div>
            <div class="col-5 d-flex justify-content-end">
              <button class="btn btn-success d-flex align-items-center" @click.prevent="saveDevice()" :disabled="saveBtnDisabled">
                <i v-if="!saveBtnDisabled" class="bx bx-check-double font-size-16"></i>
                <i v-else class="bx bx-loader bx-spin font-size-16"></i>
                <span class="ms-1">Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- end card -->
    </div>
  </div>
</template>
